import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import tr from 'date-fns/locale/tr';

// Türkçe lokalizasyonunu kaydet
registerLocale('tr', tr);
setDefaultLocale('tr');

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = (e) => setMatches(e.matches);
    setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
};

// DD/MM/YYYY -> YYYY-MM-DD (timezone-güvenli, Date objesi yok)
const toISO = (ddmmyyyy) => {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return '';
  const [d, m, y] = ddmmyyyy.split('/');
  if (!d || !m || !y) return '';
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

// YYYY-MM-DD -> DD/MM/YYYY (timezone-güvenli, Date objesi yok)
const fromISO = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
};

const pad = (n) => String(n).padStart(2, '0');

// maxDate (opsiyonel, Date): takvim/native picker'ın izin verdiği en geç tarih.
// Verilmezse varsayılan bugündür (geriye dönük uyumlu — mevcut çağrılar etkilenmez).
// Gelecek tarih gerektiren alanlar (ör. kapsam dışı bayram girişi) ileri bir Date geçer.
const CustomDatePicker = ({ value, onChange, onBlur, maxDate }) => {
  const [localDate, setLocalDate] = useState(value);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const isLargeScreen = useMediaQuery('(min-width: 768px)');
  const dateInputRef = useRef(null);
  const buttonRef = useRef(null);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    setLocalDate(value);
  }, [value]);

  const formatDate = useCallback((input) => {
    const cleaned = input.replace(/[^\d]/g, '');
    const match = cleaned.match(/^(\d{1,2})(\d{1,2})?(\d{0,4})?$/);
    if (match) {
      const [, day, month, year] = match;
      if (day) {
        if (month) {
          if (year) {
            return `${day}/${month}/${year}`;
          }
          return `${day}/${month}`;
        }
        return day;
      }
    }
    return cleaned;
  }, []);

  const isValidDate = useCallback((dateString) => {
    if (dateString.length !== 10) return false;
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }, []);

  const handleDateChange = useCallback((e) => {
    let input = e.target.value;
    if (input.replace(/[^\d]/g, '').length <= 8) {
      const formatted = formatDate(input);
      setLocalDate(formatted);
      onChange(formatted);
      setDateError('');
    }
  }, [formatDate, onChange]);

  const handleDateBlur = useCallback((e) => {
    let formatted = e.target.value;
    if (formatted.length === 8 && !formatted.includes('/')) {
      formatted = `${formatted.slice(0, 2)}/${formatted.slice(2, 4)}/${formatted.slice(4)}`;
    } else if (formatted.length > 5 && formatted.indexOf('/') === 2 && formatted.lastIndexOf('/') === 2) {
      formatted = `${formatted.slice(0, 5)}/${formatted.slice(5)}`;
    }
    setLocalDate(formatted);
    onChange(formatted);

    if (formatted.length === 10 && !isValidDate(formatted)) {
      setDateError('Geçersiz tarih');
    } else {
      setDateError('');
    }

    onBlur(e);
  }, [onChange, onBlur, isValidDate]);

  const handleCalendarClick = useCallback((e) => {
    e.stopPropagation();
    setIsDatePickerOpen(true);
  }, []);

  const handleNativeDateChange = useCallback((e) => {
    const formatted = fromISO(e.target.value);
    if (formatted) {
      setLocalDate(formatted);
      onChange(formatted);
      setDateError('');
    }
  }, [onChange]);

  const handleDatePickerChange = useCallback((date) => {
    if (date) {
      const formattedDate = date.toLocaleDateString('tr-TR', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\./g, '/');
      setLocalDate(formattedDate);
      onChange(formattedDate);
      setDateError('');
    }
    setIsDatePickerOpen(false);
  }, [onChange]);

  const parseDate = useCallback((dateString) => {
    if (dateString.length !== 10) return null;
    const [day, month, year] = dateString.split(/[-./]/);
    return new Date(year, month - 1, day);
  }, []);

  // useMemo ile DatePicker props'larını optimize ettik
  const datePickerProps = useMemo(() => ({
    selected: localDate ? parseDate(localDate) : null,
    onChange: handleDatePickerChange,
    onClickOutside: () => setIsDatePickerOpen(false),
    inline: true,
    dateFormat: "dd/MM/yyyy",
    locale: "tr",
    showYearDropdown: true,
    showMonthDropdown: true,
    scrollableYearDropdown: true,
    scrollableMonthYearDropdown: true,
    yearDropdownItemNumber: 100,
    minDate: new Date(new Date().getFullYear() - 100, 0, 1),
    maxDate: maxDate instanceof Date && !isNaN(maxDate) ? maxDate : new Date()
  }), [localDate, handleDatePickerChange, parseDate, maxDate]);

  const now = new Date();
  const effMax = maxDate instanceof Date && !isNaN(maxDate) ? maxDate : now;
  const maxISO = `${effMax.getFullYear()}-${pad(effMax.getMonth() + 1)}-${pad(effMax.getDate())}`;
  const minISO = `${now.getFullYear() - 100}-01-01`;

  return (
    <div>
      <div className="relative">
        <input
          type="text"
          ref={dateInputRef}
          value={localDate}
          onChange={handleDateChange}
          onBlur={handleDateBlur}
          className={`w-full rounded-lg border bg-white px-3 py-2.5 pr-12 text-sm text-slate-900 shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ${
            dateError ? 'border-red-500' : 'border-slate-300'
          }`}
          placeholder="GG/AA/YYYY"
        />
        {!isLargeScreen && (
          <>
            <span className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center text-indigo-600">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-xl" />
            </span>
            <input
              type="date"
              value={toISO(localDate)}
              max={maxISO}
              min={minISO}
              onChange={handleNativeDateChange}
              aria-label="Takvimden tarih seç"
              tabIndex={-1}
              className="absolute right-0 top-0 bottom-0 w-12 z-10 opacity-0 appearance-none cursor-pointer"
            />
          </>
        )}
        {isLargeScreen && (
          <button
            ref={buttonRef}
            onClick={handleCalendarClick}
            className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center text-indigo-600 hover:bg-indigo-100 rounded-r-md transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="text-xl" />
          </button>
        )}
        {dateError && (
          <div className="absolute top-0 right-0 mt-2 mr-2">
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {dateError}
            </span>
          </div>
        )}
        {isLargeScreen && isDatePickerOpen && (
          <DatePicker {...datePickerProps} />
        )}
      </div>
    </div>
  );
};

export default React.memo(CustomDatePicker);
